// src/components/travel/flightUtils.js

import { flightDataService } from '../services/flightDataService';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';

/**
 * Field configuration for flight data entry
 */
export const FLIGHT_FIELDS = {
    airline: {
        name: 'airline',
        label: 'Airline',
        type: 'select',
        required: false,
        options: async () => await flightDataService.getAirlinesList(
            currentUser?.uid, 
            tripId
        ),
        allowCustom: true,
        renderOption: (option) => ({
            label: option.name,
            value: option.code,
            // Add metadata for UI rendering
            meta: {
                isRecent: !!option.recent,
                source: option.source || 'standard',
                inCurrentTrip: !!option.inCurrentTrip
            }
        }),
        onSelect: (option) => {
            // Track selection if user is logged in
            if (currentUser?.uid && option) {
                flightDataService.trackRecentlyUsedAirline(
                    currentUser.uid,
                    tripId,
                    option
                );
            }
            return option;
        },
        placeholder: 'Select or enter airline name'
    },
    flightNumber: {
        name: 'flightNumber',
        label: 'Flight Number',
        type: 'text',
        required: false,
        placeholder: 'e.g. AA123',
        validation: value => flightDataService.validateFlightNumber(value)
    },
    departureAirport: {
        name: 'departureAirport',
        label: 'Departure Airport',
        type: 'select',
        required: false,
        options: () => flightDataService.getAirportsList(),
        allowCustom: true,
        placeholder: 'Enter airport code or name',
        validation: value => flightDataService.validateAirportCode(value)
    },
    departureTime: {
        name: 'departureTime',
        label: 'Departure Time',
        type: 'datetime',
        required: false,
        placeholder: 'Select departure date and time'
    },
    departureTimezone: {
        name: 'departureTimezone',
        label: 'Departure Timezone',
        type: 'timezone',
        required: false,
        placeholder: 'Select timezone'
    },
    arrivalAirport: {
        name: 'arrivalAirport',
        label: 'Arrival Airport',
        type: 'select',
        required: false,
        options: () => flightDataService.getAirportsList(),
        allowCustom: true,
        placeholder: 'Enter airport code or name',
        validation: value => flightDataService.validateAirportCode(value)
    },
    arrivalTime: {
        name: 'arrivalTime',
        label: 'Arrival Time',
        type: 'datetime',
        required: false,
        placeholder: 'Select arrival date and time'
    },
    arrivalTimezone: {
        name: 'arrivalTimezone',
        label: 'Arrival Timezone',
        type: 'timezone',
        required: false,
        placeholder: 'Select timezone'
    },
    bookingAgent: {
        name: 'bookingAgent',
        label: 'Booking Agent/Website',
        type: 'text',
        required: false,
        placeholder: 'e.g. Expedia, Direct with Airline'
    },
    bookingLink: {
        name: 'bookingLink',
        label: 'Booking Link',
        type: 'url',
        required: false,
        placeholder: 'Link to booking/reservation page'
    },
    bookingReference: {
        name: 'bookingReference',
        label: 'Booking Reference',
        type: 'text',
        required: false,
        placeholder: 'Enter booking/confirmation number'
    },
    seatNumber: {
        name: 'seatNumber',
        label: 'Seat Number',
        type: 'text',
        required: false,
        placeholder: 'Enter seat assignment'
    },
    class: {
        name: 'class',
        label: 'Class',
        type: 'select',
        required: false,
        options: () => [
            { code: 'economy', label: 'Economy' },
            { code: 'premium_economy', label: 'Premium Economy' },
            { code: 'business', label: 'Business' },
            { code: 'first', label: 'First' }
        ],
        allowCustom: true,
        placeholder: 'Select or enter class'
    }
};

/**
 * Infer missing fields from provided data
 */
export const inferMissingFields = (data) => {
    const inferred = { ...data };
    
    // Try to infer airline from flight number
    if (data.flightNumber && !data.airline) {
        const parsed = flightDataService.parseFlightNumber(data.flightNumber);
        if (parsed && parsed.airline) {
            inferred.airline = parsed.airline;
            inferred._inferred = inferred._inferred || {};
            inferred._inferred.airline = true;
        }
    }

    // Try to infer airport timezones
    if (data.departureAirport) {
        const airportInfo = flightDataService.validateAirportCode(data.departureAirport);
        if (airportInfo.isValid && airportInfo.info?.timezone && !data.departureTimezone) {
            inferred.departureTimezone = airportInfo.info.timezone;
            inferred._inferred = inferred._inferred || {};
            inferred._inferred.departureTimezone = true;
        }
    }

    if (data.arrivalAirport) {
        const airportInfo = flightDataService.validateAirportCode(data.arrivalAirport);
        if (airportInfo.isValid && airportInfo.info?.timezone && !data.arrivalTimezone) {
            inferred.arrivalTimezone = airportInfo.info.timezone;
            inferred._inferred = inferred._inferred || {};
            inferred._inferred.arrivalTimezone = true;
        }
    }

    return inferred;
};

/**
 * Validate flight data and return any errors
 */
export const validateFlightData = (data) => {
    const errors = {};
    const warnings = {};
    
    // Validate flight number if provided
    if (data.flightNumber) {
        const flightNumberValidation = flightDataService.validateFlightNumber(data.flightNumber);
        if (!flightNumberValidation.isValid) {
            errors.flightNumber = flightNumberValidation.error;
        }
    }

    // Validate airports
    if (data.departureAirport) {
        const departureValidation = flightDataService.validateAirportCode(data.departureAirport);
        if (!departureValidation.isValid) {
            errors.departureAirport = departureValidation.error;
        }
    }

    if (data.arrivalAirport) {
        const arrivalValidation = flightDataService.validateAirportCode(data.arrivalAirport);
        if (!arrivalValidation.isValid) {
            errors.arrivalAirport = arrivalValidation.error;
        }
    }

    // Validate times if both are provided
    if (data.departureTime && data.arrivalTime) {
        const deptTime = new Date(data.departureTime).getTime();
        const arrTime = new Date(data.arrivalTime).getTime();
        
        if (arrTime <= deptTime) {
            errors.arrivalTime = "Arrival time must be after departure time";
        } else {
            const duration = (arrTime - deptTime) / (1000 * 60 * 60); // hours
            if (duration > 20) {
                warnings.arrivalTime = "Flight duration exceeds 20 hours. Please verify times are correct.";
            }
        }
    }

    // Validate booking link if provided
    if (data.bookingLink && !data.bookingLink.match(/^https?:\/\/.+/)) {
        errors.bookingLink = "Please enter a valid URL starting with http:// or https://";
    }

    // Check minimum required data
    const hasMinimumData = data.flightNumber || 
        (data.departureAirport && data.arrivalAirport && (data.departureTime || data.arrivalTime));

    if (!hasMinimumData) {
        errors._form = 'Please provide either a flight number or departure/arrival information';
    }

    return {
        hasErrors: Object.keys(errors).length > 0,
        hasWarnings: Object.keys(warnings).length > 0,
        errors,
        warnings
    };
};

/**
 * Normalize flight data for storage
 */
export const normalizeFlightData = (formData) => {
    const normalized = { ...formData };

    // Normalize flight number
    if (normalized.flightNumber) {
        const parsed = flightDataService.parseFlightNumber(normalized.flightNumber);
        if (parsed) {
            normalized.flightNumber = parsed.fullNumber;
        }
    }

    // Normalize airport codes
    if (normalized.departureAirport) {
        const airport = flightDataService.validateAirportCode(normalized.departureAirport);
        if (airport.isValid) {
            normalized.departureAirport = airport.code;
        }
    }

    if (normalized.arrivalAirport) {
        const airport = flightDataService.validateAirportCode(normalized.arrivalAirport);
        if (airport.isValid) {
            normalized.arrivalAirport = airport.code;
        }
    }

    // Normalize booking link
    if (normalized.bookingLink && !normalized.bookingLink.startsWith('http')) {
        normalized.bookingLink = `https://${normalized.bookingLink}`;
    }

    // Clean up inferred data markers
    delete normalized._inferred;

    return normalized;
};

export const useFlightFields = (tripId) => {
    const { currentUser } = useAuth();
    const { effectiveUserId } = useTrip();

    return {
        airline: {
            name: 'airline',
            label: 'Airline',
            type: 'select',
            required: false,
            options: async () => await flightDataService.getAirlinesList(
                effectiveUserId,
                tripId
            ),
            allowCustom: true,
            renderOption: (option) => ({
                label: option.name,
                value: option.code,
                // Add metadata for UI rendering
                meta: {
                    isRecent: !!option.recent,
                    source: option.source || 'standard',
                    inCurrentTrip: !!option.inCurrentTrip
                }
            }),
            onSelect: (option) => {
                // Track selection if user is logged in
                if (effectiveUserId && option) {
                    flightDataService.trackRecentlyUsedAirline(
                        effectiveUserId,
                        tripId,
                        option
                    );
                }
                return option;
            },
            placeholder: 'Select or enter airline name'
        },
        flightNumber: {
            name: 'flightNumber',
            label: 'Flight Number',
            type: 'text',
            required: false,
            placeholder: 'e.g. AA123',
            validation: value => flightDataService.validateFlightNumber(value)
        },
        departureAirport: {
            name: 'departureAirport',
            label: 'Departure Airport',
            type: 'select',
            required: false,
            options: () => flightDataService.getAirportsList(),
            allowCustom: true,
            placeholder: 'Enter airport code or name',
            validation: value => flightDataService.validateAirportCode(value)
        },
        departureTime: {
            name: 'departureTime',
            label: 'Departure Time',
            type: 'datetime',
            required: false,
            placeholder: 'Select departure date and time'
        },
        departureTimezone: {
            name: 'departureTimezone',
            label: 'Departure Timezone',
            type: 'timezone',
            required: false,
            placeholder: 'Select timezone'
        },
        arrivalAirport: {
            name: 'arrivalAirport',
            label: 'Arrival Airport',
            type: 'select',
            required: false,
            options: () => flightDataService.getAirportsList(),
            allowCustom: true,
            placeholder: 'Enter airport code or name',
            validation: value => flightDataService.validateAirportCode(value)
        },
        arrivalTime: {
            name: 'arrivalTime',
            label: 'Arrival Time',
            type: 'datetime',
            required: false,
            placeholder: 'Select arrival date and time'
        },
        arrivalTimezone: {
            name: 'arrivalTimezone',
            label: 'Arrival Timezone',
            type: 'timezone',
            required: false,
            placeholder: 'Select timezone'
        },
        bookingAgent: {
            name: 'bookingAgent',
            label: 'Booking Agent/Website',
            type: 'text',
            required: false,
            placeholder: 'e.g. Expedia, Direct with Airline'
        },
        bookingLink: {
            name: 'bookingLink',
            label: 'Booking Link',
            type: 'url',
            required: false,
            placeholder: 'Link to booking/reservation page'
        },
        bookingReference: {
            name: 'bookingReference',
            label: 'Booking Reference',
            type: 'text',
            required: false,
            placeholder: 'Enter booking/confirmation number'
        },
        seatNumber: {
            name: 'seatNumber',
            label: 'Seat Number',
            type: 'text',
            required: false,
            placeholder: 'Enter seat assignment'
        },
        class: {
            name: 'class',
            label: 'Class',
            type: 'select',
            required: false,
            options: () => [
                { code: 'economy', label: 'Economy' },
                { code: 'premium_economy', label: 'Premium Economy' },
                { code: 'business', label: 'Business' },
                { code: 'first', label: 'First' }
            ],
            allowCustom: true,
            placeholder: 'Select or enter class'
        }
    };
};